import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  CircularProgress,
  Grid,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon 
} from '@mui/icons-material';
import axios from 'axios';
import baseURL from '../../../baseURL';
import MasterPageShell, {
  masterPaperSx,
  masterListPaperSx,
  masterSectionTitleSx,
  masterPrimaryBtnSx,
  masterFieldSx,
  masterTableHeadCellSx,
} from '../../common/MasterPageShell';

const TownVillageManagement = ({ isModal = false, onClose, onTownVillageCreated }) => {
  const [townVillages, setTownVillages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tvPage, setTvPage] = useState(0);
  const [tvRowsPerPage, setTvRowsPerPage] = useState(50);
  const [tvTotalDocs, setTvTotalDocs] = useState(0);

  // Create/Edit Town/Village State
  const [townVillageForm, setTownVillageForm] = useState({
    _id: '',
    village: '',
    atPost: '',
    taluka: '',
    district: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [createError, setCreateError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');

  // Delete Town/Village State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [townVillageToDelete, setTownVillageToDelete] = useState(null);

  const fetchTownVillagesPage = async (page = 0, rowsPerPage = 50, search = '') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseURL}/town-villages`, {
        params: { page: page + 1, limit: rowsPerPage, ...(search && { search }) },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const res = response.data;
      setTownVillages(res.data || []);
      setTvTotalDocs(res.totalDocs ?? (res.data || []).length);
      setTvPage(page);
      setTvRowsPerPage(rowsPerPage);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch town/villages');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTownVillagesPage(0, tvRowsPerPage, searchTerm);
  }, [searchTerm]);

  const handleTvPageChange = (event, newPage) => {
    fetchTownVillagesPage(newPage, tvRowsPerPage, searchTerm);
  };

  const handleTvRowsPerPageChange = (event) => {
    const newRows = parseInt(event.target.value, 10);
    setTvRowsPerPage(newRows);
    fetchTownVillagesPage(0, newRows, searchTerm);
  };





  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log('📝 Input change:', { name, value });

    setTownVillageForm(prev => {
      const newForm = {
        ...prev,
        [name]: value
      };
      console.log('📋 Updated form data:', newForm);
      return newForm;
    });
    

  };

  const handleCreateTownVillage = async (e) => {
    e.preventDefault();
    console.log('🚀 handleCreateTownVillage called');
    console.log('📝 Form data being sent:', townVillageForm);
    console.log('🔧 Is editing mode:', isEditing);
    
    // Validate that at least village is provided
    if (!townVillageForm.village || townVillageForm.village.trim() === '') {
      setCreateError('Village name is required');
      setCreateLoading(false);
      return;
    }
    
    // Warn if all fields have the same value (might be a mistake)
    const { village, atPost, taluka, district } = townVillageForm;
    if (village === atPost && atPost === taluka && taluka === district && village !== '') {
      console.log('⚠️ Warning: All fields have the same value. This might be intentional or a mistake.');
    }
    
    setCreateLoading(true);
    setCreateError(null);

    try {
      const token = localStorage.getItem('token');
      console.log('🔑 Token available:', !!token);
      let response;

      if (isEditing) {
        console.log('✏️ Updating existing town/village with ID:', townVillageForm._id);
        // Update existing town/village
        response = await axios.patch(`${baseURL}/town-villages/${townVillageForm._id}`, townVillageForm, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        console.log('✅ Update response:', response.data);

      } else {
        console.log('🆕 Creating new town/village');
        response = await axios.post(`${baseURL}/town-villages`, townVillageForm, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }

      // Reset form
      setTownVillageForm({ _id: '', village: '', atPost: '', taluka: '', district: '' });
      setIsEditing(false);
      setCreateSuccess(true);
      setCreateLoading(false);
      fetchTownVillagesPage(tvPage, tvRowsPerPage, searchTerm);

      // If in modal mode, call the callback
      if (isModal && onTownVillageCreated) {
        console.log('📞 Calling onTownVillageCreated callback with:', response.data.data);
        onTownVillageCreated(response.data.data);
      }
    } catch (err) {
      console.error('❌ Error in handleCreateTownVillage:', err);
      console.error('❌ Error response:', err.response);
      console.error('❌ Error message:', err.message);
      setCreateError(err.response?.data?.message || 'Failed to create/update town/village');
      setCreateLoading(false);
    }
  };

  const handleEditTownVillage = (townVillage) => {
    setTownVillageForm({
      _id: townVillage._id,
      village: townVillage.village,
      atPost: townVillage.atPost,
      taluka: townVillage.taluka,
      district: townVillage.district
    });
    setIsEditing(true);
  };

  const handleDeleteTownVillage = async () => {
    if (!townVillageToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${baseURL}/town-villages/${townVillageToDelete._id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setDeleteDialogOpen(false);
      setTownVillageToDelete(null);
      setCreateSuccess(true);
      fetchTownVillagesPage(tvPage, tvRowsPerPage, searchTerm);
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to delete town/village');
    }
  };

  const handleCloseSnackbar = () => {
    setCreateSuccess(false);
    setCreateError(null);
  };

  // Filter town villages based on search term
  const filteredTownVillages = townVillages; // Server returns filtered by searchTerm

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  const pageBody = (
      <Container maxWidth="lg" sx={{ mt: isModal ? 0 : 0, px: isModal ? 0 : undefined }}>
        <Grid container spacing={3}>
          {/* Modal Header with Close Button */}
          {isModal && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Town/Village Management</Typography>
                <Button 
                  variant="outlined" 
                  onClick={onClose}
                  color="secondary"
                >
                  Close
                </Button>
              </Box>
            </Grid>
          )}
          
          {/* Create/Edit Town/Village Form */}
          <Grid item xs={12} md={isModal ? 6 : 4}>
            <Paper elevation={0} sx={masterPaperSx}>
              <Typography sx={masterSectionTitleSx}>
                {isEditing ? 'Edit town / village' : 'Create town / village'}
              </Typography>
              <Box component="form" onSubmit={handleCreateTownVillage} noValidate>
                  <TextField
                      fullWidth
                      margin="normal"
                      label="Village"
                      name="village"
                      value={townVillageForm.village}
                      onChange={handleInputChange}
                      required
                      sx={masterFieldSx}
                    />
                    <TextField
                      fullWidth
                      margin="normal"
                      label="At Post"
                      name="atPost"
                      value={townVillageForm.atPost}
                      onChange={handleInputChange}
                      required
                      sx={masterFieldSx}
                    />
                    <TextField
                      fullWidth
                      margin="normal"
                      label="Taluka"
                      name="taluka"
                      value={townVillageForm.taluka}
                      onChange={handleInputChange}
                      required
                      sx={masterFieldSx}
                    />
                    <TextField
                      fullWidth
                      margin="normal"
                      label="District"
                      name="district"
                      value={townVillageForm.district}
                      onChange={handleInputChange}
                      required
                      sx={masterFieldSx}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={createLoading}
                      sx={masterPrimaryBtnSx}
                      startIcon={createLoading ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                      {isEditing ? 'Update town / village' : 'Create town / village'}
                    </Button>
                    {isEditing ? (
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setIsEditing(false);
                          setTownVillageForm({ _id: '', village: '', atPost: '', taluka: '', district: '' });
                        }}
                        sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 650 }}
                      >
                        Cancel
                      </Button>
                    ) : null}
              </Box>
            </Paper>
          </Grid>

          {/* Town/Village List */}
          <Grid item xs={12} md={isModal ? 6 : 8}>
            <Paper elevation={0} sx={masterListPaperSx}>
              <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                <Typography sx={{ ...masterSectionTitleSx, mb: 1 }}>
                  Town / village list ({filteredTownVillages.length} of {tvTotalDocs})
                </Typography>
                <TextField
                  fullWidth
                  label="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  placeholder="Village, at post, taluka, or district…"
                  sx={{ ...masterFieldSx, mb: 1 }}
                />
              </Box>
              <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={masterTableHeadCellSx}>Village</TableCell>
                    <TableCell sx={masterTableHeadCellSx}>At Post</TableCell>
                    <TableCell sx={masterTableHeadCellSx}>Taluka</TableCell>
                    <TableCell sx={masterTableHeadCellSx}>District</TableCell>
                    <TableCell align="right" sx={masterTableHeadCellSx}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTownVillages.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                        {searchTerm ? `No matches for “${searchTerm}”` : 'No town/villages found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTownVillages.map((townVillage) => (
                      <TableRow key={townVillage._id} hover>
                        <TableCell sx={{ fontWeight: 650 }}>{townVillage.village}</TableCell>
                        <TableCell>{townVillage.atPost}</TableCell>
                        <TableCell>{townVillage.taluka}</TableCell>
                        <TableCell>{townVillage.district}</TableCell>
                        <TableCell align="right">
                          <IconButton 
                            color="primary"
                            size="small"
                            onClick={() => handleEditTownVillage(townVillage)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            color="error"
                            size="small"
                            onClick={() => {
                              setTownVillageToDelete(townVillage);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={tvTotalDocs}
                page={tvPage}
                onPageChange={handleTvPageChange}
                rowsPerPage={tvRowsPerPage}
                onRowsPerPageChange={handleTvRowsPerPageChange}
                rowsPerPageOptions={[25, 50, 100, 200]}
                labelRowsPerPage="Rows per page:"
              />
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this town/village?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button 
              color="error" 
              onClick={handleDeleteTownVillage}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success/Error Snackbar */}
        <Snackbar
          open={createSuccess || !!createError}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={createError ? 'error' : 'success'}
            sx={{ width: '100%' }}
          >
            {createError || 'Operation successful!'}
          </Alert>
        </Snackbar>
      </Container>
  );

  if (isModal) {
    return <Box sx={{ p: 1 }}>{pageBody}</Box>;
  }

  return (
    <MasterPageShell title="Town / Village" subtitle="Master data">
      {pageBody}
    </MasterPageShell>
  );
};

export default TownVillageManagement;